<div class="live-chat" [class.live-chat--popout]="isPopout" [class.live-chat--hidden]="detachedHidden() || handedOff()">
  <button
    type="button"
    class="live-chat__fab"
    [ngStyle]="fabCss()"
    (pointerdown)="onFabPointerDown($event)"
    [attr.aria-expanded]="panelOpen()"
    [class.live-chat__fab--open]="panelOpen()"
    [class.live-chat__fab--alert]="onsiteVisualAlert() || vendorCheckoutNotices().length > 0"
    [class.live-chat__fab--dragging]="fabDragging()"
    [class.live-chat__fab--leaving]="fabLeaving()"
    [attr.aria-label]="
      fabUnreadCount() > 0
        ? 'Open job chat, ' + (fabUnreadCount() > 99 ? '99+' : fabUnreadCount()) + ' unread'
        : 'Open job chat'
    "
    [attr.title]="
      dragEnabled()
        ? canDetach()
          ? 'Job chat — drag to move, or drop it outside the browser to open it in its own window'
          : 'Job chat — drag to move'
        : 'Job chat'
    "
  >
    @if (fabUnreadCount() > 0) {
      <span class="live-chat__fab-badge" aria-hidden="true">{{
        fabUnreadCount() > 99 ? '99+' : fabUnreadCount()
      }}</span>
    }
    <svg
      class="live-chat__fab-svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
    >
      <path
        d="M12 3C7.03 3 3 6.58 3 11c0 2.13 1.04 4.06 2.73 5.5L4 21l4.87-1.62A9.06 9.06 0 0 0 12 19c4.97 0 9-3.58 9-8s-4.03-8-9-8Z"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  @if (isPopout && windowMode() === 'pill') {
    <!-- The detached window as a small pill (Nahid, 1 Nov): click it and the window grows into the whole chat. -->
    <button
      type="button"
      class="live-chat__pill"
      [class.live-chat__pill--alert]="onsiteVisualAlert() || vendorCheckoutNotices().length > 0"
      data-testid="live-chat-pill"
      (click)="expandWindow()"
      [attr.aria-label]="'Open the job chat. ' + pillLabel()"
      [attr.title]="'Job chat: ' + pillLabel() + ' (click to open)'"
    >
      <!-- Just the bubble: nothing else in the window, so it reads as a floating chat button. -->
      <span class="live-chat__pill-bubble" aria-hidden="true">
        @if (fabUnreadCount() > 0) {
          <span class="live-chat__fab-badge">{{ fabUnreadCount() > 99 ? '99+' : fabUnreadCount() }}</span>
        }
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3C7.03 3 3 6.58 3 11c0 2.13 1.04 4.06 2.73 5.5L4 21l4.87-1.62A9.06 9.06 0 0 0 12 19c4.97 0 9-3.58 9-8s-4.03-8-9-8Z"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </button>
  }

  @if (panelOpen()) {
    <div
      class="live-chat__panel"
      [ngStyle]="panelCss()"
      [class.live-chat__panel--in-thread]="panelView() === 'thread'"
      [class.live-chat__panel--alert]="onsiteVisualAlert()"
      [class.live-chat__panel--movable]="dragEnabled()"
      [class.live-chat__panel--popout]="isPopout"
      [class.live-chat__panel--dragging]="panelDragging()"
      (pointerdown)="onPanelPointerDown($event)"
      role="dialog"
      aria-label="Job chat"
    >
      @if (canDetach()) {
        <!-- Nahid (3 Nov): one click does what dropping the bubble outside the browser does. -->
        <button
          type="button"
          class="live-chat__btn-float"
          data-testid="live-chat-float"
          (click)="detachFromIcon($event)"
          aria-label="Float the chat as a bubble outside the browser"
          title="Float the chat as a bubble outside the browser"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="5" width="14" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
            <path d="M13 3h8v8M21 3l-9 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      }
      <div class="live-chat__shell" [class.live-chat__shell--has-float]="canDetach()">
        @if (vendorCheckoutNotices().length > 0) {
          <!-- Across the top of the pop-up, above the inbox and the estimate: stays until dismissed (Nahid, 28 Oct). -->
          <div class="live-chat__checkout-stack" role="alert" data-testid="vendor-checkout-notices">
            @for (notice of vendorCheckoutNotices(); track notice.id) {
              <div class="live-chat__checkout-banner" data-testid="vendor-checkout-notice">
                <svg class="live-chat__checkout-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 3 2.5 20h19L12 3Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
                  <path d="M12 10v4.5M12 17.2v.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                </svg>
                <span class="live-chat__checkout-text"><strong>Vendor checked out.</strong> {{ notice.text }}</span>
                <a
                  class="live-chat__checkout-link"
                  [routerLink]="['/job', notice.jobKey, 'estimates']"
                  target="_blank"
                  rel="noopener"
                  >Open job</a
                >
                <button
                  type="button"
                  class="live-chat__checkout-close"
                  aria-label="Dismiss"
                  (click)="dismissVendorCheckoutNotice(notice.id)"
                >&times;</button>
              </div>
            }
          </div>
        }
        @if (responseAlerts().length > 0) {
          <!-- Nahid (4 Nov): a vendor (or manager) response still waiting on RFI. Bold red, across the top, until handled. -->
          <div class="live-chat__response-stack" role="alert" data-testid="response-alerts">
            @for (a of responseAlerts(); track a.key) {
              <div class="live-chat__response-banner" data-testid="response-alert">
                <span class="live-chat__response-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 5h16v11H8l-4 4V5Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                    <path d="M12 8.5v3.5M12 14.6v.1" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </span>
                <span class="live-chat__response-text">
                  <strong class="live-chat__response-title">{{ a.title }}</strong>
                  <span class="live-chat__response-where">
                    {{ a.jobLabel || 'Job' }}@if (a.vendorLabel) { · {{ a.vendorLabel }} } · {{ responseAlertTime(a) }}
                  </span>
                  @if (a.detail) {
                    <span class="live-chat__response-detail">{{ a.detail }}</span>
                  }
                </span>
                @if (!isResponseAlertOnScreen(a)) {
                  <button type="button" class="live-chat__response-open" (click)="openResponseAlert(a)">Open</button>
                }
                <button
                  type="button"
                  class="live-chat__response-handled"
                  data-testid="response-handled"
                  title="Clear this alert. It also clears when you approve, decline, counter or reply."
                  (click)="markResponseHandled(a)"
                >
                  Mark handled
                </button>
              </div>
            }
          </div>
        }
        <div class="live-chat__split" [class.live-chat__split--resizing]="colResizing()">
          <aside class="live-chat__sidebar">
            <div class="live-chat__sidebar-head">
              <div class="live-chat__sidebar-lead">
                <!-- Lance (28 Oct): the X that closes the whole pop-up sits to the left of "Inbox". -->
                @if (!isPopout) {
                  <button
                    type="button"
                    class="live-chat__btn-close"
                    data-testid="live-chat-close"
                    (click)="closePanel()"
                    aria-label="Close the chat pop-up"
                    title="Close the chat pop-up"
                  >
                    &times;
                  </button>
                }
                <span class="live-chat__sidebar-title">
                  @if (dragEnabled()) {
                    <span class="live-chat__drag-grip" aria-hidden="true" title="Drag the header to move">
                      <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                        <circle cx="2" cy="2" r="1.4" />
                        <circle cx="8" cy="2" r="1.4" />
                        <circle cx="2" cy="7" r="1.4" />
                        <circle cx="8" cy="7" r="1.4" />
                        <circle cx="2" cy="12" r="1.4" />
                        <circle cx="8" cy="12" r="1.4" />
                      </svg>
                    </span>
                  }
                  Inbox
                </span>
              </div>
              @if (canDetach()) {
                <!-- Detach (Nahid, 1 Nov): the chat in its own window, on any screen; every tab then hides its bubble. -->
                <button
                  type="button"
                  class="live-chat__btn-detach"
                  data-testid="live-chat-detach"
                  (click)="popOut()"
                  aria-label="Open the chat in its own window"
                  title="Open the chat in its own window"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              }
            </div>

            @if (showNewJobEntry()) {
              <div class="live-chat__new-job-bar">
                @if (newJobKeyInvalid) {
                  <p class="live-chat__new-job-error">Check the job ID and try again.</p>
                }
                <div class="live-chat__new-job-row">
                  <input
                    id="lc-new-job"
                    class="live-chat__new-job-input"
                    type="text"
                    [(ngModel)]="newJobKeyInput"
                    placeholder="Paste job GUID"
                    (keydown.enter)="openNewJobByKey()"
                    autocomplete="off"
                  />
                  <button type="button" class="live-chat__new-job-go" (click)="openNewJobByKey()">
                    Open
                  </button>
                </div>
              </div>
            }

            @if (onsiteVisualAlert()) {
              <div class="live-chat__onsite-banner" role="status">
                <span class="live-chat__onsite-banner-icon" aria-hidden="true"></span>
                <span>New on-site request and responses</span>
              </div>
            }

            <div class="live-chat__search-bar">
              <input
                type="text"
                class="live-chat__search-input"
                [ngModel]="jobSearchTerm()"
                (ngModelChange)="onJobSearchInput($event)"
                placeholder="Search by job PO…"
                aria-label="Search jobs by PO"
                autocomplete="off"
              />
            </div>

            <div class="live-chat__filters" role="group" aria-label="Filter on-site approvals">
              @for (f of onsiteFilters; track f.key) {
                <button
                  type="button"
                  class="live-chat__filter"
                  [class.live-chat__filter--active]="onsiteFilter() === f.key"
                  [attr.aria-pressed]="onsiteFilter() === f.key"
                  (click)="setOnsiteFilter(f.key)"
                >
                  {{ f.label }} <span class="live-chat__filter-count">{{ onsiteFilterCount(f.key) }}</span>
                </button>
              }
            </div>

            <div class="live-chat__list">
              @if (listLoadError) {
                <p class="live-chat__list-hint live-chat__list-hint--error">
                  Could not load chats. Sign in or check the API.
                </p>
              }
              @if (!listLoadError && conversations.length === 0) {
                <p class="live-chat__list-hint">
                  No open on-site approvals.<br />Approved and declined ones are on each job's Estimates page.<br />Tap <strong>+</strong> to open any job.
                </p>
              }
              @if (!listLoadError && conversations.length > 0 && filteredConversations().length === 0) {
                <p class="live-chat__list-hint">
                  @if (jobSearchTerm().trim()) {
                    No on-site approvals match "{{ jobSearchTerm() }}".
                  } @else {
                    No on-site approvals in this filter.
                  }
                </p>
              }
              <!-- Vendors of the same job sit together inside one bold outline (Lance, 28 Oct); the group is placed where
                   the job's newest approval is, and an approval that has left the inbox is simply not in it (Nahid, 29 Oct). -->
              @for (g of conversationGroups(); track g.key) {
              <div
                class="live-chat__group"
                [class.live-chat__group--multi]="g.items.length > 1"
                [attr.data-testid]="g.items.length > 1 ? 'job-group' : null"
                [attr.role]="g.items.length > 1 ? 'group' : null"
                [attr.aria-label]="g.items.length > 1 ? g.poLabel + ', ' + g.items.length + ' vendors on this job' : null"
              >
                @if (g.items.length > 1) {
                  <div class="live-chat__group-head" data-testid="job-group-head">
                    <span class="live-chat__group-po">{{ g.poLabel }}</span>
                    <span class="live-chat__group-count">{{ g.items.length }} vendors on this job</span>
                  </div>
                }
                @for (c of g.items; track c.jobKey + '|' + (c.vendorKey ?? 'g')) {
                <button
                  type="button"
                  class="live-chat__list-item"
                  [class.live-chat__list-item--active]="isActiveConversation(c)"
                  [class.live-chat__list-item--unread]="isThreadUnread(c)"
                  [class.live-chat__list-item--onsite-new]="isOnsiteNew(c)"
                  [class.live-chat__list-item--new]="isNewThread(c)"
                  [class.live-chat__list-item--returned]="isReturnedFromManager(c)"
                  [class.live-chat__list-item--waiting-manager]="!!waitingOnManager(c)"
                  [class.live-chat__list-item--leaving]="isLeaving(c)"
                  [attr.aria-hidden]="isLeaving(c) ? 'true' : null"
                  [attr.tabindex]="isLeaving(c) ? -1 : null"
                  (click)="openThread(c)"
                >
                  <div class="live-chat__list-top">
                    <div class="live-chat__list-top-main">
                      <span class="live-chat__list-name" [attr.title]="vendorLabel(c)">{{ vendorLabel(c) }}</span>
                    </div>
                    <div class="live-chat__list-top-end">
                      <span class="live-chat__list-time">{{
                        formatListTime(c.lastMessageUtc)
                      }}</span>
                      <!-- Re-created on each new response, so the orange pulse starts again. -->
                      @for (tick of [pulseTick(c)]; track tick) {
                        @if (getUnreadMessageCount(c) > 0) {
                          <span
                            class="live-chat__list-count"
                            [attr.aria-label]="getUnreadMessageCount(c) + ' unread'"
                          >
                            {{ unreadCountLabel(c) }}
                          </span>
                        } @else if (isNewThread(c)) {
                          <span class="live-chat__list-new-dot" aria-label="New" data-testid="new-dot"></span>
                        }
                      }
                    </div>
                  </div>
                  <div class="live-chat__list-job">
                    {{ jobLabel(c) }}
                    @if (isOnsiteNew(c)) {
                      <span class="live-chat__list-onsite-tag">On-site</span>
                    }
                    @if (c.submittedFromWeb) {
                      <span class="live-chat__list-web-tag">Submitted from web</span>
                    }
                    @if (isReturnedFromManager(c)) {
                      <span class="live-chat__list-returned-tag" data-testid="returned-from-manager">Returned by manager</span>
                    }
                    @if (closedTagLabel(c); as closedTag) {
                      <span class="live-chat__list-closed-tag">{{ closedTag }}</span>
                    }
                  </div>
                  @if (waitingOnManager(c); as wait) {
                    <div class="live-chat__list-waiting" data-testid="waiting-on-manager">
                      <span class="live-chat__list-waiting-icon" aria-hidden="true">&#9203;</span>
                      <span>Waiting for uplining manager: <strong>{{ managerNames(wait) }}</strong></span>
                    </div>
                  }
                  <div class="live-chat__list-meta">{{ listPreview(c) }}</div>
                </button>
                }
              </div>
              }
            </div>
          </aside>

          <div class="live-chat__workspace">
            @if (panelView() !== 'thread') {
              <div class="live-chat__empty">
                <div class="live-chat__empty-card">
                  <div class="live-chat__empty-icon" aria-hidden="true">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M8 10h8M8 14h5M5 4h14a2 2 0 0 1 2 2v12l-4-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                  <h3 class="live-chat__empty-title">Your conversations</h3>
                  <p>
                    Select a thread from the inbox, or use <strong>+</strong> to open a job by ID.
                  </p>
                </div>
              </div>
            }

            @if (checkedOutHere(); as gone) {
              <aside
                class="live-chat__estimate-col"
                [class.live-chat__estimate-col--sized]="estimateColWidth() != null"
                [style.flexBasis.px]="estimateColWidth()"
                aria-label="Vendor checked out"
              >
                <header class="live-chat__estimate-head">
                  <div class="live-chat__estimate-head-main">
                    <span class="live-chat__estimate-kicker">Vendor checked out</span>
                    <h4 class="live-chat__estimate-title">No longer an on-site approval</h4>
                  </div>
                </header>
                <div class="live-chat__closed live-chat__closed--checkout" role="status" data-testid="vendor-checkout-pane">
                  <p class="live-chat__closed-text">
                    <strong>{{ gone.text }}</strong>
                    The pop-up is for on-site approvals only, so it's no longer listed in your inbox.
                  </p>
                  <div class="live-chat__closed-actions">
                    <a
                      class="live-chat__closed-btn"
                      [routerLink]="['/job', gone.jobKey, 'estimates']"
                      target="_blank"
                      rel="noopener"
                      >Open job</a
                    >
                    <button type="button" class="live-chat__closed-btn" (click)="backToList()">Back to inbox</button>
                  </div>
                </div>
              </aside>
            }

            @if (panelView() === 'thread' && selectedJobKey() && !checkedOutHere()) {
              <aside
                class="live-chat__estimate-col"
                [class.live-chat__estimate-col--sized]="estimateColWidth() != null"
                [style.flexBasis.px]="estimateColWidth()"
                [class.live-chat__estimate-col--returned]="!!returnedForSelected()"
                aria-label="Vendor estimate"
                (click)="onEstimateColumnClick()"
              >
                @if (waitingOnManagerForSelected(); as wait) {
                  <div class="live-chat__manager-wait" role="status" data-testid="waiting-on-manager-banner">
                    <div class="live-chat__manager-wait-title">
                      <span aria-hidden="true">&#9203;</span> Waiting for the uplining manager's response
                    </div>
                    <div class="live-chat__manager-wait-name">{{ managerNames(wait) }}</div>
                    <div class="live-chat__manager-wait-meta">
                      Sent by {{ wait.requestedByName || 'the account manager' }} at {{ wait.requestedOn | date: 'MMM d, h:mm a' }}
                      @if (wait.sentOn && wait.sentOn !== wait.requestedOn) {
                        <span>· last email {{ wait.sentOn | date: 'h:mm a' }}</span>
                      }
                      — do not approve the vendor until the manager decides.
                    </div>
                  </div>
                }
                @if (returnedForSelected(); as ret) {
                  <div class="live-chat__manager-return live-chat__manager-return--hint" role="status" data-testid="returned-hint">
                    <strong>Returned by {{ ret.managerName || 'your manager' }}.</strong>
                    Click here to read their message.
                  </div>
                }
                @if (managerReturnNote(); as note) {
                  <div class="live-chat__manager-return" role="status" data-testid="manager-return-note">
                    <div class="live-chat__manager-return-head">
                      <strong>{{ note.managerName || 'Your manager' }} sent this back — edit needed</strong>
                      <button
                        type="button"
                        class="live-chat__updated-by-close"
                        aria-label="Dismiss"
                        (click)="$event.stopPropagation(); dismissManagerReturnNote()"
                      >
                        &times;
                      </button>
                    </div>
                    <p class="live-chat__manager-return-text">{{ note.managerRemark }}</p>
                    @if (note.decidedOn) {
                      <span class="live-chat__manager-return-meta">{{ note.decidedOn | date: 'MMM d, h:mm a' }}</span>
                    }
                  </div>
                }
                <header class="live-chat__estimate-head">
                  <div class="live-chat__estimate-head-main">
                    <span class="live-chat__estimate-kicker">Estimate review</span>
                    <h4 class="live-chat__estimate-title">Vendor estimate</h4>
                  </div>
                  @if (estimateStatusLabel) {
                    <span
                      class="live-chat__estimate-pill legacy-job-chat-estimate-pill"
                      [ngClass]="estimateStatusClasses"
                      >{{ estimateStatusLabel }}</span
                    >
                  }
                  <!--
                    Hidden (Nahid, 8 Oct): nothing in the pop-up should send the admin to V1. Kept, not removed, in case
                    it comes back pointing at V2.
                  @if (showEstimateBody && vendorEstimatePageUrl()) {
                    <button
                      type="button"
                      class="live-chat__view-job live-chat__est-v1-link"
                      title="Open the full vendor estimate in legacy admin (V1)"
                      (click)="openVendorEstimateInV1()"
                    >
                      Open vendor estimate
                    </button>
                  }
                  -->
                </header>

                @if (closedOnsiteBanner(); as closed) {
                  <div class="live-chat__closed" [class.live-chat__closed--declined]="closed === 'declined'" role="status">
                    <p class="live-chat__closed-text">
                      <strong>{{ closed === 'approved' ? 'Approved.' : 'Declined.' }}</strong>
                      @if (closedOnsiteOptionText(closed); as optionText) {
                        {{ optionText }}
                      }
                      This on-site approval is complete, so it's no longer listed in your inbox. You'll find it on the job's Estimates page.
                      @if (closed === 'declined' && currentEstimate?.hasUnbilledIncurredLines && !currentEstimate?.createdInvoiceForIncurredAfterDeclined) {
                        Bill the vendor's incurred costs below before you go back (later, from the vendor estimate in V1).
                      }
                    </p>
                    <div class="live-chat__closed-actions">
                      <a
                        class="live-chat__closed-btn"
                        [routerLink]="['/job', selectedJobKey(), 'estimates']"
                        target="_blank"
                        rel="noopener"
                        >Open job</a
                      >
                      <button type="button" class="live-chat__closed-btn" (click)="backToList()">Back to inbox</button>
                    </div>
                  </div>
                }
                @if (otherViewersHere(); as viewers) {
                  @if (viewers.length) {
                    <div class="live-chat__presence" role="status">
                      Also open by {{ viewers.join(', ') }}
                    </div>
                  }
                }
                @if (selectedThreadFromWeb()) {
                  <div class="live-chat__web-origin" role="note">
                    <svg class="live-chat__web-origin-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
                    </svg>
                    <span>This on-site approval was <strong>submitted from the web</strong>.</span>
                  </div>
                }
                @if (onsiteUpdatedBy(); as upd) {
                  <div class="live-chat__updated-by" role="status">
                    <span>{{ upd.name }} {{ upd.action }} at {{ upd.at | date: 'h:mm a' }}. The estimate below is up to date.</span>
                    <button type="button" class="live-chat__updated-by-close" aria-label="Dismiss" (click)="dismissOnsiteUpdatedBy()">&times;</button>
                  </div>
                }

                @if ((currentEstimate?.options?.length ?? 0) > 1) {
                  <div class="live-chat__est-opt-row" role="tablist" aria-label="Estimate options">
                    @for (opt of currentEstimate!.options!; track opt.estimateKey) {
                      <button
                        type="button"
                        class="live-chat__est-opt-btn"
                        [class.live-chat__est-opt-btn--active]="opt.isSelected"
                        [class.live-chat__est-opt-btn--declined]="opt.status === 3"
                        [attr.aria-selected]="opt.isSelected ? 'true' : 'false'"
                        [title]="opt.optionLabel + ' · ' + optionChipLabel(opt)"
                        (click)="selectEstimateOption(opt.estimateKey)"
                      >
                        <span class="live-chat__est-opt-label">{{ opt.optionLabel }}</span>
                        <span class="live-chat__est-opt-price">{{ opt.total | currency: 'USD':'symbol':'1.0-0' }}</span>
                        <span
                          class="live-chat__est-opt-chip"
                          [class.live-chat__est-opt-chip--approved]="opt.status === 1"
                          [class.live-chat__est-opt-chip--closed]="opt.status === 3"
                          >{{ optionChipLabel(opt) }}</span
                        >
                      </button>
                    }
                  </div>
                  @if (showOptionPickNote()) {
                    <p class="live-chat__est-opt-note" role="note">
                      The vendor sent <strong>{{ estimateOptions().length }} options</strong>. Pick one to work on:
                      approving, negotiating or sending back goes ahead with that option and closes the others.
                      Decline closes all options. Get Customer Approval closes nothing: you choose which options
                      go to the customer.
                    </p>
                  }
                }

                @if (estimateLoading()) {
                  <div class="live-chat__estimate-loading">
                    <span class="live-chat__estimate-spinner" aria-hidden="true"></span>
                    <span>Loading estimate…</span>
                  </div>
                }

                @if (showEstimateEmpty()) {
                  <div class="live-chat__estimate-placeholder">
                    <div class="live-chat__estimate-placeholder-icon" aria-hidden="true">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 1 1 4 0"
                          stroke="currentColor"
                          stroke-width="1.5"
                        />
                      </svg>
                    </div>
                    <p>{{ estimateEmptyMessage }}</p>
                    <button type="button" class="live-chat__est-btn" (click)="retryEstimateLoad()">
                      Retry estimate
                    </button>
                  </div>
                }

                <!-- VE-DNE: shown up front, before Approve is tried. The button is offered only where Get Customer Approval is:
                     it opens the same create dialog without holding the vendor, or, once the customer sent the estimate
                     back, takes the existing "Edit & resend to customer" path. -->
                @if (dneInfo(); as dne) {
                  @if (estimateHasActions()) {
                  <app-dne-banner
                    class="live-chat__est-dne-banner"
                    [message]="dne.message"
                    [requiredTotal]="dne.requiredTotal"
                    [customerDne]="dne.customerDne"
                    [maxVendorAmount]="dne.maxVendorAmount"
                    [minimumProfit]="dne.minimumProfit"
                    [markupPercent]="dne.markupPercent"
                    [showAction]="dneBannerShowAction()"
                    [actionLabel]="dneBannerActionLabel()"
                    (createCustomerEstimate)="onDneBannerCreateCustomerEstimate()"
                  />
                  }
                }

                @if (vendorAgreedBannerVisible()) {
                  <div class="live-chat__estimate-vendor-edit live-chat__estimate-vendor-agreed" role="status">
                    {{ vendorAgreedBannerText }}
                  </div>
                } @else if (showEstimateVendorEdit) {
                  <div class="live-chat__estimate-vendor-edit">{{ estimateVendorEditMsg }}</div>
                }

                @if (showEstimateDelta && !vendorAgreedBannerVisible()) {
                  <div class="live-chat__estimate-delta">
                    <span class="live-chat__estimate-delta-label">{{ estimateDeltaText }}</span>
                  </div>
                }

                @if (showEstimateOriginal && estimateOriginalHtmlSafe) {
                  <details class="live-chat__estimate-original">
                    <summary>View {{ estimateOriginalSummaryLabel }}</summary>
                    <div
                      class="live-chat__estimate-body"
                      [innerHTML]="estimateOriginalHtmlSafe"
                    ></div>
                  </details>
                }

                @if (currentEstimate?.estimateKey) {
                  <details class="live-chat__estimate-original live-chat__rounds" [open]="negotiationHistoryOpen" (toggle)="onNegotiationHistoryToggle($event)">
                    <summary>Negotiation history</summary>
                    @if (negotiationRoundsLoading()) {
                      <p class="live-chat__rounds-note">Loading…</p>
                    } @else if (negotiationRoundsError()) {
                      <p class="live-chat__rounds-note">{{ negotiationRoundsError() }}</p>
                      <button type="button" class="live-chat__est-btn live-chat__rounds-refresh" (click)="loadNegotiationRounds(true, true)">
                        Refresh
                      </button>
                    } @else if (negotiationRoundsForCurrent(); as rounds) {
                      @if (rounds.length === 0) {
                        <p class="live-chat__rounds-note">No rounds saved yet for this estimate.</p>
                      }
                      @for (round of rounds; track round.roundKey) {
                        <div class="live-chat__round" [class.live-chat__round--admin]="round.party === 'admin'">
                          <div class="live-chat__round-head">
                            <span class="live-chat__round-title">{{ round.roundNumber }}. {{ negotiationRoundLabel(round) }}</span>
                            <span class="live-chat__round-total">{{ round.totalAmount ?? 0 | currency: 'USD' }}</span>
                          </div>
                          <div class="live-chat__round-meta">{{ negotiationRoundTime(round) | date: 'MMM d, h:mm a' }}</div>
                          @if (round.note) {
                            <div class="live-chat__round-meta">{{ round.note }}</div>
                          }
                          <ul class="live-chat__round-lines">
                            @for (line of round.lines; track line.lineKey) {
                              <li [class.live-chat__round-line--off]="line.isNotNeeded">
                                <span class="live-chat__round-line-name">{{ line.itemName || (line.kind === 'labor' ? 'Labor' : 'Line') }}</span>
                                <span>{{ negotiationRoundLineQty(line) }} × {{ line.rate ?? 0 | currency: 'USD' }}</span>
                                <span class="live-chat__round-line-total">{{ line.rowTotal ?? 0 | currency: 'USD' }}</span>
                              </li>
                            }
                          </ul>
                        </div>
                      }
                      <button type="button" class="live-chat__est-btn live-chat__rounds-refresh" (click)="loadNegotiationRounds(true, true)">
                        Refresh
                      </button>
                    }
                  </details>
                }

                @if (showEstimateBody && estimateHtmlSafe) {
                  <div
                    class="live-chat__estimate-body"
                    [class.live-chat__estimate-body--vendor-compare]="
                      estimateCompareMode === 'vendor'
                    "
                    [class.live-chat__estimate-body--admin-compare]="
                      estimateCompareMode === 'admin'
                    "
                    [class.live-chat__estimate-body--mixed-compare]="
                      estimateCompareMode === 'mixed'
                    "
                    [innerHTML]="estimateHtmlSafe"
                  ></div>
                }

                @if (estimateHasActions()) {
                  <div class="live-chat__estimate-actions">
                    @if (
                      estimateAllowedActions().includes('approve') ||
                      estimateAllowedActions().includes('negotiate')
                    ) {
                      <app-dne-vendor-scope
                        class="live-chat__dne-scope"
                        name="live-chat-dne-scope"
                        [keepAll]="dneKeepAll()"
                        [multiVendor]="vendorScopeVisible()"
                        [glow]="true"
                        (keepAllChange)="onDneKeepAllChange($event)"
                      />
                    }
                    <div class="live-chat__est-actions-row">
                      @if (
                        estimateAllowedActions().includes('approve') &&
                        !dneApproveBlocked()
                      ) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--approve"
                          (click)="openEstimateAction('approve')"
                        >
                          Approve
                        </button>
                      } @else if (
                        dneApproveBlocked() &&
                        estimateAllowedActions().includes('approve')
                      ) {
                        <!--
                          The banner above says why. Lance, 3 Nov: Approve opens the override pop-up (get the customer's
                          approval, or override with reduced profit / upline to a manager) instead of doing nothing.
                        -->
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--approve live-chat__est-btn--dne-blocked"
                          title="Above the customer DNE: approving needs the customer's approval or an override."
                          data-testid="dne-override-approve"
                          (click)="openDneOverride()"
                        >
                          Approve
                        </button>
                      } @else if (estimateApproveDisabled()) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--approve live-chat__est-btn--disabled"
                          [title]="estimateApproveDisabledReason()"
                          disabled
                        >
                          Approve
                        </button>
                      }
                      @if (estimateAllowedActions().includes('reject')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--reject"
                          (click)="openEstimateAction('reject')"
                        >
                          Reject for resubmission
                        </button>
                      }
                      @if (estimateAllowedActions().includes('negotiate')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--negotiate"
                          (click)="openEstimateAction('negotiate')"
                        >
                          Negotiate
                        </button>
                      }
                      @if (estimateAllowedActions().includes('decline')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--decline"
                          (click)="openEstimateAction('decline')"
                        >
                          Decline
                        </button>
                      }
                      @if (estimateAllowedActions().includes('get_customer_approval')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--customer"
                          (click)="openEstimateAction('get_customer_approval')"
                        >
                          {{ customerApprovalButtonLabel() }}
                        </button>
                      }
                      @if (estimateAllowedActions().includes('approve_on_behalf_and_vendor')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--customer"
                          (click)="openEstimateAction('approve_on_behalf_and_vendor')"
                        >
                          Approve on behalf of customer and approve vendor
                        </button>
                      }
                      @if (estimateAllowedActions().includes('checkout_for_approval_later')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--customer-outline"
                          (click)="openEstimateAction('checkout_for_approval_later')"
                        >
                          Check tech out for approval later
                        </button>
                      }
                      @if (estimateAllowedActions().includes('go_home')) {
                        <button
                          type="button"
                          class="live-chat__est-btn live-chat__est-btn--go-home"
                          (click)="openEstimateAction('go_home')"
                        >
                          Go Home
                        </button>
                      }
                    </div>
                  </div>
                } @else if (estimateShowApproved()) {
                  <div class="live-chat__estimate-actions">
                    <p class="live-chat__est-done-msg">This on-site estimate has been approved.</p>
                  </div>
                } @else if (optionClosedInFavorOf()) {
                  <div class="live-chat__estimate-actions">
                    <p class="live-chat__est-done-msg">
                      This option is closed: RFI went ahead with {{ optionClosedInFavorOf() }}.
                    </p>
                  </div>
                } @else if (estimateShowDeclined()) {
                  <div class="live-chat__estimate-actions">
                    <p class="live-chat__est-done-msg">This on-site estimate has been declined.</p>
                    @if (currentEstimate?.createdInvoiceForIncurredAfterDeclined) {
                      <p class="live-chat__est-incurred-note">
                        A vendor invoice was created from incurred costs on this estimate.
                      </p>
                    } @else if (currentEstimate?.hasUnbilledIncurredLines) {
                      <button
                        type="button"
                        class="live-chat__est-btn live-chat__est-btn--incurred"
                        (click)="openEstimateAction('bill-incurred')"
                      >
                        Bill incurred costs
                      </button>
                    }
                  </div>
                } @else if (estimateShowWaitingVendor()) {
                  <div class="live-chat__estimate-actions">
                    <p class="live-chat__est-done-msg">Waiting for vendor to edit and resubmit.</p>
                  </div>
                } @else if (estimateShowNeedsReload()) {
                  <div class="live-chat__estimate-actions">
                    <p class="live-chat__est-done-msg">
                      Vendor has updated this estimate. Reload the thread or contact support if
                      action buttons are missing.
                    </p>
                  </div>
                }

                @if (negotiatePanelOpen) {
                  <div class="live-chat__negotiate-panel legacy-job-chat-negotiate-panel">
                    <div class="live-chat__negotiate-head legacy-job-chat-negotiate-head">
                      <span>Counter — edit lines</span>
                      <button
                        type="button"
                        (click)="closeNegotiatePanel()"
                        aria-label="Close negotiate"
                      >
                        &times;
                      </button>
                    </div>
                    @if (negotiateLoading) {
                      <p class="live-chat__negotiate-loading">Loading lines…</p>
                    } @else if (negotiateLines.length === 0) {
                      <p class="live-chat__negotiate-loading">
                        {{ negotiateError || 'Could not load estimate lines.' }}
                      </p>
                    } @else {
                      <table class="live-chat__neg-table legacy-job-chat-neg-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Rate</th>
                            <th>Qty/Hrs</th>
                            <th>Tech</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (line of negotiateLines; track line.detailKey) {
                            <tr>
                              <td class="live-chat__neg-item legacy-job-chat-neg-item">
                                {{ line.itemName }}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  inputmode="decimal"
                                  class="live-chat__neg-input legacy-job-chat-neg-rate"
                                  [class.live-chat__neg-input--readonly]="
                                    isNegotiateRateReadOnly(line)
                                  "
                                  [(ngModel)]="line.editRate"
                                  [readonly]="isNegotiateRateReadOnly(line)"
                                  (keydown)="onNegInputKeydown($event, true)"
                                  (blur)="onNegInputBlur(line, 'editRate')"
                                />
                              </td>
                              @if (line.labor === 'labor') {
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    inputmode="decimal"
                                    class="live-chat__neg-input legacy-job-chat-neg-hour"
                                    [(ngModel)]="line.editHour"
                                    (keydown)="onNegInputKeydown($event, true)"
                                    (blur)="onNegInputBlur(line, 'editHour')"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    inputmode="numeric"
                                    class="live-chat__neg-input legacy-job-chat-neg-tech"
                                    [(ngModel)]="line.editTech"
                                    (keydown)="onNegInputKeydown($event, false)"
                                    (blur)="onNegInputBlur(line, 'editTech')"
                                  />
                                </td>
                              } @else {
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    inputmode="decimal"
                                    class="live-chat__neg-input legacy-job-chat-neg-qty"
                                    [(ngModel)]="line.editQty"
                                    (keydown)="onNegInputKeydown($event, true)"
                                    (blur)="onNegInputBlur(line, 'editQty')"
                                  />
                                </td>
                                <td>—</td>
                              }
                            </tr>
                          }
                        </tbody>
                      </table>
                      <label class="live-chat__neg-remark-label legacy-job-chat-neg-remark-label">
                        Note to vendor (optional)
                      </label>
                      <textarea
                        class="live-chat__neg-remark legacy-job-chat-neg-remark"
                        rows="3"
                        [(ngModel)]="negotiateRemark"
                        placeholder="Optional message…"
                      ></textarea>
                    }
                    @if (negotiateError) {
                      <p class="live-chat__action-error">{{ negotiateError }}</p>
                    }
                    <div class="live-chat__negotiate-foot legacy-job-chat-negotiate-foot">
                      <button
                        type="button"
                        class="live-chat__est-btn live-chat__est-btn--negotiate"
                        (click)="submitNegotiate()"
                        [disabled]="
                          negotiateSubmitting || negotiateLoading || !negotiateLines.length
                        "
                      >
                        {{ negotiateSubmitting ? 'Saving…' : 'Send revised estimate' }}
                      </button>
                    </div>
                  </div>
                }
              </aside>

              @if (checkedOutHere() || (panelView() === 'thread' && selectedJobKey())) {
                <!-- Drag to resize the estimate (middle) vs. the message panel (Nahid, 2 Nov). -->
                <div
                  class="live-chat__col-resizer"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Drag to resize the estimate and message panels"
                  title="Drag to resize"
                  (pointerdown)="onColResizeStart($event)"
                ></div>
              }

              <main class="live-chat__main">
                <header class="live-chat__thread-head">
                  <div class="live-chat__thread-brand">
                    <span class="live-chat__thread-kicker">Conversation</span>
                    <h3 class="live-chat__thread-name">{{ threadVendorName() }}</h3>
                    <p class="live-chat__thread-sub">{{ threadScopeLine() }}</p>
                  </div>
                  <a
                    class="live-chat__view-job"
                    [routerLink]="['/job', selectedJobKey(), 'estimates']"
                    target="_blank"
                    rel="noopener"
                  >
                    Open job
                  </a>
                </header>

                @if (showAgentTabs()) {
                  <div class="live-chat__main-tabs">
                    <button
                      type="button"
                      class="live-chat__main-tab"
                      [class.live-chat__main-tab--active]="mainPaneView() === 'chat'"
                      (click)="setMainPaneView('chat')"
                    >
                      Chat
                    </button>
                    <button
                      type="button"
                      class="live-chat__main-tab"
                      [class.live-chat__main-tab--active]="mainPaneView() === 'negotiate'"
                      (click)="setMainPaneView('negotiate')"
                    >
                      <span class="live-chat__main-tab-dot"></span>Negotiate
                    </button>
                  </div>
                }

                @if (mainPaneView() === 'negotiate') {
                  <div class="live-chat__agent-panel">
                    <div class="live-chat__agent-head">
                      <span class="live-chat__agent-eyebrow">{{
                        agentNegotiation()?.aiUnavailable ? 'Suggested counter' : 'Agent Recommendation'
                      }}</span>
                      <span class="live-chat__agent-meta">
                        Round {{ agentPanelSummary().roundNumber }} ·
                        {{ agentPanelSummary().statusLabel }}
                      </span>
                    </div>

                    @if (agentLoading()) {
                      <p class="live-chat__agent-hint" role="status">
                        Generating negotiating-agent recommendations…
                      </p>
                    } @else if (agentError() && !agentNegotiation()?.recommendationsReady) {
                      <div
                        class="live-chat__agent-feedback live-chat__agent-feedback--error"
                        role="alert"
                      >
                        <span>{{ agentError() }}</span>
                        <button
                          type="button"
                          class="live-chat__agent-edit-save"
                          (click)="retryAgentNegotiation()"
                        >
                          Retry
                        </button>
                      </div>
                    } @else if (agentNegotiation() && !agentNegotiation()?.showPanel) {
                      <p class="live-chat__agent-hint">
                        Negotiating-agent recommendations are not available for this estimate in its
                        current state.
                      </p>
                    } @else if (agentNegotiation()?.recommendationsReady) {
                      @if (agentNegotiation()?.aiUnavailable) {
                        <p class="live-chat__agent-fallback" role="status">
                          AI suggestions aren't available right now, so this is RFI's standard counter: labor hours
                          lowered to {{ agentNegotiation()?.fallbackPercent ?? 85 }}% of the vendor's labor, trip and
                          materials unchanged. Use it as is, or edit any line, then send.
                        </p>
                      }
                      @if (agentStatusMessage()) {
                        <p class="live-chat__agent-feedback" role="status">
                          {{ agentStatusMessage() }}
                        </p>
                      }
                      @if (agentNoMoreCounters()) {
                        <p class="live-chat__agent-notice live-chat__agent-notice--stop" role="status">
                          {{ negotiateNoMoreCountersNote }}
                        </p>
                      } @else if (vendorAgreedBannerVisible()) {
                        <p class="live-chat__agent-notice" role="status">
                          {{ negotiateVendorAgreedNote }}
                        </p>
                      }

                      <div class="live-chat__agent-summary">
                        <div class="live-chat__agent-summary-top">
                          <div class="live-chat__agent-summary-main">
                            <span class="live-chat__agent-was">{{
                              formatEstimateMoney(agentPanelSummary().vendorTotalWas)
                            }}</span>
                            <span class="live-chat__agent-arrow" aria-hidden="true">
                              <svg viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M4 12h14M13 6l6 6-6 6"
                                  stroke="currentColor"
                                  stroke-width="2.6"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </span>
                            <span class="live-chat__agent-now">{{
                              formatEstimateMoney(agentPanelSummary().counterTotal)
                            }}</span>
                            <span
                              class="live-chat__agent-delta"
                              [class.is-down]="agentPanelSummary().deltaPct < 0"
                              [class.is-up]="agentPanelSummary().deltaPct > 0"
                            >
                              {{ agentPanelSummary().deltaPct }}%
                            </span>
                            <span class="live-chat__agent-savings">
                              saves {{ formatEstimateMoney(agentPanelSummary().savingsAmount) }}
                            </span>
                          </div>
                        </div>
                        @if (agentPanelSummary().firstSubmittedTotal; as first) {
                          <p class="live-chat__agent-first" data-testid="agent-first-submitted">
                            Vendor first submitted {{ formatEstimateMoney(first) }}
                          </p>
                        }
                        @if (!agentNegotiation()?.aiUnavailable) {
                        <div class="live-chat__agent-conf-row">
                          <span class="live-chat__agent-conf-label">Overall confidence</span>
                          <div class="live-chat__agent-conf-track">
                            <div
                              class="live-chat__agent-conf-fill"
                              [style.width.%]="agentPanelSummary().confidencePct"
                            ></div>
                          </div>
                          <span class="live-chat__agent-conf-pct"
                            >{{ agentPanelSummary().confidencePct }}%</span
                          >
                        </div>
                        }
                      </div>

                      @if (agentNegotiation()?.vendorRaisedCost) {
                        <div class="live-chat__raised" role="alert" data-testid="vendor-raised-cost">
                          <b>The cost went up.</b>
                          The vendor sent back {{ formatEstimateMoney(agentNegotiation()?.vendorRaisedTo) }}, which is
                          {{ formatEstimateMoney((agentNegotiation()?.vendorRaisedTo ?? 0) - (agentNegotiation()?.vendorRaisedFrom ?? 0)) }}
                          more than your counter of {{ formatEstimateMoney(agentNegotiation()?.vendorRaisedFrom) }}.
                          @if ((agentNegotiation()?.vendorAddedLines ?? 0) > 0) {
                            {{ agentNegotiation()?.vendorAddedLines }} new
                            {{ agentNegotiation()?.vendorAddedLines === 1 ? 'line was' : 'lines were' }} added.
                          }
                        </div>
                      }

                      @for (line of agentPanelLines(); track line.lineItemId) {
                        <app-estimate-negotiation-line
                          [line]="line"
                          [suggestedByRfi]="!!agentNegotiation()?.aiUnavailable"
                          [decision]="agentLineDecisionStateFor(line.lineItemId)?.decision ?? null"
                          [editedValue]="agentLineDecisionStateFor(line.lineItemId)?.editedValue ?? null"
                          [editedQty]="agentLineDecisionStateFor(line.lineItemId)?.editedQty ?? null"
                          [editedTechs]="agentLineDecisionStateFor(line.lineItemId)?.editedTechs ?? null"
                          [editedHoursPerTech]="agentLineDecisionStateFor(line.lineItemId)?.editedHoursPerTech ?? null"
                          [message]="agentLineMessage(line.lineItemId)"
                          [messageEdited]="isAgentLineMessageEdited(line.lineItemId)"
                          [expandMode]="isAgentLineExpandOpen(line.lineItemId, 'edit') ? 'edit' : isAgentLineExpandOpen(line.lineItemId, 'info') ? 'info' : isAgentLineExpandOpen(line.lineItemId, 'msg') ? 'msg' : null"
                          [disabled]="agentSubmitting()"
                          [compact]="true"
                          (decisionChange)="setAgentLineDecision(line.lineItemId, $event)"
                          (changeDecision)="reopenAgentLineDecision(line.lineItemId)"
                          (editConfirm)="onAgentLineEditConfirm(line, $event)"
                          (expandModeChange)="onAgentLineExpandModeChange(line, $event)"
                          (messageChange)="setAgentLineMessage(line.lineItemId, $event)"
                          (messageReset)="resetAgentLineMessage(line)"
                        />
                        @if (agentLineAgreedPrice(line.lineItemId)) {
                          <p class="live-chat__agent-agreed-price" role="note">{{ agreedPriceNote }}</p>
                        }
                      }

                      @if (agentPassthroughLines().length > 0) {
                        <p class="live-chat__agent-passthrough">
                          <span>
                            {{ agentPanelPassthroughLabel() }} —
                            <strong>{{ formatEstimateMoney(agentPanelPassthroughTotal()) }}</strong>
                            already incurred, passed through unchanged
                          </span>
                        </p>
                      }

                      @if (agentPanelDneTotal() > 0) {
                        <div class="live-chat__agent-dne">
                          <div class="live-chat__agent-dne-top">
                            <span class="live-chat__agent-dne-label">Customer DNE</span>
                            <span class="live-chat__agent-dne-status">
                              <span class="live-chat__agent-dot"></span
                              >{{ agentPanelDneStatusLabel() }}
                            </span>
                          </div>
                          <div class="live-chat__agent-dne-body">
                            <div class="live-chat__agent-dne-ring-wrap">
                              <svg
                                viewBox="0 0 36 36"
                                class="live-chat__agent-dne-ring"
                                aria-hidden="true"
                              >
                                <circle
                                  class="live-chat__agent-dne-ring-track"
                                  cx="18"
                                  cy="18"
                                  r="15.5"
                                ></circle>
                                <circle
                                  class="live-chat__agent-dne-ring-fill"
                                  cx="18"
                                  cy="18"
                                  r="15.5"
                                  style="stroke-dasharray: 97.4"
                                  [style.stroke-dashoffset]="
                                    gaugeDashOffset(agentPanelDneCommittedPct())
                                  "
                                ></circle>
                              </svg>
                              <span class="live-chat__agent-dne-ring-pct"
                                >{{ agentPanelDneCommittedPct() }}%</span
                              >
                            </div>
                            <div class="live-chat__agent-dne-text">
                              <span class="live-chat__agent-dne-committed">
                                {{ formatEstimateMoney(agentPanelDneCommittedAmount()) }}
                              </span>
                              <span class="live-chat__agent-dne-of">
                                revised estimate of
                                {{ formatEstimateMoney(agentPanelDneTotal()) }} customer DNE
                              </span>
                            </div>
                          </div>
                        </div>
                      }

                      <!-- Lance (28 Oct): the same message box as the Estimates tab. It goes to the vendor with every
                           counter (Use Counter and Accept All); the admin may edit it, and clearing it sends no message. -->
                      <div class="live-chat__agent-note">
                        <label class="live-chat__agent-note-label" for="lc-agent-general-message"
                          >General message to vendor</label
                        >
                        <textarea
                          id="lc-agent-general-message"
                          class="live-chat__agent-note-input"
                          data-testid="agent-general-message"
                          rows="3"
                          maxlength="1000"
                          [ngModel]="agentGeneralMessage()"
                          (ngModelChange)="agentGeneralMessage.set($event)"
                          [disabled]="agentSubmitting()"
                          placeholder="Optional. Clear this to send the counter without a message."
                        ></textarea>
                      </div>
                      @if (agentError()) {
                        <!-- Next to the buttons, not at the top: the buttons are at the bottom of a long panel, so a failed send
                             was invisible after the click (26 Oct, 8006 counter). -->
                        <p
                          class="live-chat__agent-feedback live-chat__agent-feedback--error"
                          role="alert"
                        >
                          {{ agentError() }}
                        </p>
                      }
                      <div class="live-chat__agent-actions">
                        <button
                          type="button"
                          class="live-chat__agent-btn live-chat__agent-btn--secondary"
                          [disabled]="!canSubmitAgentCounter()"
                          (click)="acceptAllAgentRecommendations()"
                        >
                          {{ agentSubmitting() ? 'Sending…' : 'Accept All' }}
                        </button>
                        <button
                          type="button"
                          class="live-chat__agent-btn live-chat__agent-btn--primary"
                          [disabled]="!canSubmitAgentCounter()"
                          (click)="sendAgentCounter()"
                        >
                          {{ agentSubmitting() ? 'Sending…' : 'Use Counter' }}
                        </button>
                      </div>
                    } @else {
                      <p class="live-chat__agent-hint" role="status">
                        The negotiating agent is preparing recommendations for this estimate.
                      </p>
                    }
                  </div>
                }

                @if (mainPaneView() === 'chat') {
                  @if (listStatusMessage()) {
                    <div class="live-chat__status">{{ listStatusMessage() }}</div>
                  }

                  <div class="live-chat__messages-wrap">
                    <div #messagesScroll class="live-chat__messages">
                      @for (m of messages(); track m.id) {
                        @if (m.senderType === 3) {
                          <div
                            class="live-chat__msg live-chat__msg--system"
                            [ngClass]="systemLifecycleClass(m.text)"
                          >
                            <div class="live-chat__meta live-chat__meta--system">
                              <span class="live-chat__meta-name">{{
                                systemLifecycleLabel(m.text)
                              }}</span>
                              <span class="live-chat__meta-time">{{
                                formatMessageTime(m.at)
                              }}</span>
                            </div>
                            <div class="live-chat__system-card">
                              <p class="live-chat__msg-text">
                                {{ systemLifecycleDisplayText(m.text) }}
                              </p>
                              @if (m.actions?.length) {
                                <div
                                  class="live-chat__msg-actions"
                                  [class.live-chat__msg-actions--done]="m.actionsConsumed"
                                >
                                  @if (m.actionsConsumed) {
                                    <span class="live-chat__msg-action-chip"
                                      >Check out completed</span
                                    >
                                  } @else {
                                    @for (a of m.actions; track a.id) {
                                      <span class="live-chat__msg-action-chip">{{ a.label }}</span>
                                    }
                                  }
                                </div>
                              }
                            </div>
                          </div>
                        } @else {
                          <div
                            class="live-chat__msg"
                            [class.live-chat__msg--them]="m.senderType === 2"
                            [class.live-chat__msg--me]="m.senderType === 1"
                            [class.live-chat__msg--needs-reply]="isUnhandledVendorLine(m)"
                          >
                            <div
                              class="live-chat__meta"
                              [class.live-chat__meta--them]="m.senderType === 2"
                              [class.live-chat__meta--me]="m.senderType === 1"
                            >
                              <span class="live-chat__meta-name" [title]="messageSenderLabel(m)">{{
                                messageSenderLabel(m)
                              }}</span>
                              @if (isUnhandledVendorLine(m)) {
                                <span class="live-chat__needs-reply-chip">Needs reply</span>
                              }
                              <span class="live-chat__meta-time">{{
                                formatMessageTime(m.at)
                              }}</span>
                            </div>
                            <div class="live-chat__msg-inner">
                              <p class="live-chat__msg-text">{{ m.text }}</p>
                              @if (m.senderType === 1) {
                                <div class="live-chat__msg-footer">
                                  <span class="live-chat__msg-time">{{
                                    formatMessageTime(m.at)
                                  }}</span>
                                  @if (m.seenByRecipient) {
                                    <span
                                      class="live-chat__receipt live-chat__receipt--seen"
                                      title="Seen"
                                      aria-label="Seen"
                                    >
                                      <svg
                                        width="16"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M4 12.5 8.5 17 18 7.5"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        />
                                        <path
                                          d="M8 12.5 12.5 17 22 7.5"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  } @else {
                                    <span
                                      class="live-chat__receipt live-chat__receipt--sent"
                                      title="Sent"
                                      aria-label="Sent"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                      >
                                        <path
                                          d="M5 12.5 9.5 17 19 7.5"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        />
                                      </svg>
                                    </span>
                                  }
                                </div>
                              }
                            </div>
                          </div>
                        }
                      }
                    </div>
                  </div>

                  <footer class="live-chat__composer">
                    <div class="live-chat__composer-row">
                      <input
                        class="live-chat__input"
                        type="text"
                        [(ngModel)]="inputText"
                        placeholder="Message the vendor. They can reply here at any time."
                        (keydown.enter)="send()"
                        maxlength="16000"
                        autocomplete="off"
                      />
                      <button type="button" class="live-chat__send" (click)="send()">Send</button>
                    </div>
                  </footer>
                }
              </main>
            }
          </div>
        </div>
      </div>
    </div>
  }

  @if (declineIncurredModalOpen) {
    <div
      class="live-chat__incurred-modal legacy-job-chat-action-modal legacy-job-chat-incurred-modal"
      role="dialog"
      aria-modal="true"
    >
      <div class="live-chat__incurred-card legacy-job-chat-incurred-modal-card">
        <div class="live-chat__incurred-head legacy-job-chat-incurred-modal-head">
          <h4 class="live-chat__action-title">{{ declineIncurredTitle() }}</h4>
          <button
            type="button"
            class="live-chat__incurred-close legacy-job-chat-incurred-close"
            (click)="closeDeclineIncurredModal()"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <p class="live-chat__incurred-lead legacy-job-chat-incurred-lead">
          This estimate includes <strong>incurred</strong> charges (work already done). Select the
          lines you want to approve for vendor billing before declining the proposed repair scope.
        </p>
        <div class="live-chat__incurred-table-wrap legacy-job-chat-incurred-table-wrap">
          <table class="live-chat__incurred-table legacy-job-chat-incurred-table">
            <thead>
              <tr>
                <th class="live-chat__incurred-chk legacy-job-chat-incurred-chk">
                  <input
                    type="checkbox"
                    [checked]="declineIncurredAllSelected()"
                    (change)="toggleDeclineIncurredSelectAll($any($event.target).checked)"
                    aria-label="Select all incurred lines"
                  />
                </th>
                <th>Item</th>
                <th>Description</th>
                <th>Rate</th>
                <th>Qty</th>
                <th>Row total</th>
              </tr>
            </thead>
            <tbody>
              @for (line of declineIncurredLines; track line.detailKey + '|' + line.whichTable) {
                <tr>
                  <td class="live-chat__incurred-chk legacy-job-chat-incurred-chk">
                    <input type="checkbox" [(ngModel)]="line.selected" />
                  </td>
                  <td>{{ line.itemName }}</td>
                  <td>{{ line.description }}</td>
                  <td class="live-chat__incurred-num legacy-job-chat-incurred-num">
                    {{ formatEstimateMoney(line.amount) }}
                  </td>
                  <td class="live-chat__incurred-num legacy-job-chat-incurred-num">
                    {{ (line.qty ?? 0).toFixed(2) }}
                  </td>
                  <td
                    class="live-chat__incurred-num live-chat__incurred-row-total legacy-job-chat-incurred-num legacy-job-chat-incurred-row-total"
                  >
                    {{ formatEstimateMoney(line.rowTotal) }}
                  </td>
                </tr>
              }
            </tbody>
            <tfoot>
              <tr>
                <td
                  colspan="5"
                  class="live-chat__incurred-foot-label legacy-job-chat-incurred-foot-label"
                >
                  Estimate incurred total
                </td>
                <td class="live-chat__incurred-num legacy-job-chat-incurred-num">
                  {{ formatEstimateMoney(declineIncurredEstimateTotal()) }}
                </td>
              </tr>
              <tr class="live-chat__incurred-selected-row legacy-job-chat-incurred-selected-row">
                <td
                  colspan="5"
                  class="live-chat__incurred-foot-label legacy-job-chat-incurred-foot-label"
                >
                  Selected total
                </td>
                <td class="live-chat__incurred-num legacy-job-chat-incurred-num">
                  {{ formatEstimateMoney(declineIncurredSelectedTotal()) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        @if (declineIncurredStatus) {
          <p class="live-chat__incurred-status legacy-job-chat-incurred-status">
            {{ declineIncurredStatus }}
          </p>
        }
        @if (pendingInvoiceOpenUrl) {
          <p class="live-chat__incurred-invoice-link-wrap">
            <button
              type="button"
              class="live-chat__incurred-invoice-link"
              (click)="openPendingInvoiceUrl()"
            >
              Open vendor invoice in new tab
            </button>
          </p>
        }
        @if (declineIncurredError) {
          <p class="live-chat__action-error">{{ declineIncurredError }}</p>
        }
        <div class="live-chat__incurred-foot legacy-job-chat-incurred-modal-foot">
          <button
            type="button"
            class="live-chat__est-btn"
            (click)="closeDeclineIncurredModal()"
            [disabled]="declineIncurredSubmitting"
          >
            Close
          </button>
          <button
            type="button"
            class="live-chat__est-btn live-chat__est-btn--incurred-save legacy-job-chat-est-btn-incurred-save"
            (click)="saveDeclineIncurredInvoice()"
            [disabled]="declineIncurredSubmitting"
          >
            {{ declineIncurredSubmitting ? 'Saving…' : declineIncurredSaveLabel() }}
          </button>
        </div>
      </div>
    </div>
  }

  @if (actionModalOpen) {
    <div class="live-chat__action-modal" role="dialog" aria-modal="true">
      <div class="live-chat__action-modal-card">
        <h4 class="live-chat__action-title">{{ actionModalTitle }}</h4>
        @if (actionModalOptionNote(); as optionNote) {
          <p class="live-chat__action-option-note" role="note">
            {{ optionNote.lead }}<strong>{{ optionNote.strong }}</strong>{{ optionNote.rest }}
          </p>
        }
        @if (actionModalHint) {
          <p class="live-chat__action-hint">{{ actionModalHint }}</p>
        }
        <textarea
          class="live-chat__action-remark"
          rows="4"
          [(ngModel)]="actionModalRemark"
          [placeholder]="
            actionModalRemarkRequired ? 'Required — explain to the vendor…' : 'Optional remark…'
          "
        ></textarea>
        @if (actionModalError) {
          <p class="live-chat__action-error">{{ actionModalError }}</p>
        }
        <div class="live-chat__action-modal-foot">
          <button
            type="button"
            class="live-chat__est-btn"
            (click)="closeActionModal()"
            [disabled]="actionModalSubmitting"
          >
            Cancel
          </button>
          <button
            type="button"
            class="live-chat__est-btn live-chat__est-btn--approve"
            (click)="confirmEstimateAction()"
            [disabled]="actionModalSubmitting"
          >
            {{ actionModalSubmitting ? 'Saving…' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  }
  @if (dneOverrideOpen()) {
    <div class="live-chat__action-modal" role="dialog" aria-modal="true" aria-labelledby="lcDneOverrideTitle" data-testid="dne-override-modal">
      <div class="live-chat__action-modal-card live-chat__dne-override">
        @switch (dneOverrideStep()) {
          @case ('loading') {
            <h4 id="lcDneOverrideTitle" class="live-chat__action-title">Checking the customer DNE…</h4>
          }
          @case ('choice') {
            <h4 id="lcDneOverrideTitle" class="live-chat__action-title">Not enough on the customer DNE</h4>
            @if (dneOverrideEval(); as ev) {
              <p class="live-chat__dne-override-alert" role="alert">{{ ev.popupMessage }}</p>
              <dl class="live-chat__dne-override-nums">
                <div><dt>Vendor amount</dt><dd>{{ ev.vendorTotal | currency: 'USD' }}</dd></div>
                <div><dt>Customer revised DNE</dt><dd>{{ ev.customerDne | currency: 'USD' }}</dd></div>
                <div><dt>Minimum profit required</dt><dd>{{ (ev.minimumProfitAmount ?? 0) | currency: 'USD' }}</dd></div>
                <div>
                  <dt>Profit if approved</dt>
                  <dd [class.live-chat__dne-override-neg]="ev.profitAmount < 0">
                    {{ ev.profitAmount | currency: 'USD' }} ({{ ev.profitMarginPercent ?? 0 | number: '1.0-2' }}% margin)
                  </dd>
                </div>
              </dl>
              @if (dneOverrideNeedsManager()) {
                <p class="live-chat__dne-override-manager-note" role="note" data-testid="dne-override-manager-note">
                  @if (ev.vendorAtOrAboveDne) {
                    The vendor amount is at or above the customer DNE, so a manager must approve it.
                  } @else {
                    The profit ({{ ev.profitMarginPercent ?? 0 | number: '1.0-2' }}% margin) is under this customer's minimum of
                    {{ ev.floorMarginPercent | number: '1.0-2' }}%, so a manager must approve it.
                  }
                </p>
              }
            }
            @if (dneOverrideError()) {
              <p class="live-chat__action-error">{{ dneOverrideError() }}</p>
            }
            <div class="live-chat__dne-override-choices">
              <button type="button" class="live-chat__est-btn live-chat__est-btn--approve" (click)="dneOverrideGetCustomerApproval()">
                Get customer approval now
              </button>
              @if (dneOverrideEval()) {
                <button
                  type="button"
                  class="live-chat__est-btn live-chat__dne-override-danger"
                  data-testid="dne-override-choose"
                  (click)="dneOverrideChooseOverride()"
                >
                  {{
                    dneOverrideNeedsManager()
                      ? 'Override — send to my manager for approval'
                      : 'Override and approve vendor anyway with reduced profit'
                  }}
                </button>
              }
            </div>
            <div class="live-chat__action-modal-foot">
              <button type="button" class="live-chat__est-btn" (click)="closeDneOverride()">Cancel</button>
            </div>
          }
          @case ('override') {
            @if (dneOverrideEval(); as ev) {
              <h4 id="lcDneOverrideTitle" class="live-chat__action-title">
                {{ dneOverrideNeedsManager() ? 'Manager approval required' : 'Override with reduced profit' }}
              </h4>
              @if (ev.vendorAtOrAboveDne) {
                <p class="live-chat__dne-override-alert" role="alert">{{ ev.atOrAboveDneWarning }}</p>
                <label class="live-chat__dne-override-label" for="lcDneReason">Customer approval / reason (required)</label>
                <textarea
                  id="lcDneReason"
                  class="live-chat__action-remark"
                  rows="3"
                  [(ngModel)]="dneOverrideReason"
                  placeholder="e.g. Verbal approval from Jane Smith (store manager) at 2:10 PM; or: this customer allows after-the-fact approvals"
                ></textarea>
              }
              @if (dneOverrideNeedsManager()) {
                <p class="live-chat__action-hint">
                  The profit ({{ ev.profitMarginPercent ?? 0 | number: '1.0-2' }}% margin) is under this customer's minimum of
                  {{ ev.floorMarginPercent | number: '1.0-2' }}%, so a manager has to approve it.
                  @if (ev.managerRecipientCount > 0) {
                    It will be sent to {{ ev.managerRecipientCount }} manager{{ ev.managerRecipientCount === 1 ? '' : 's' }} by email.
                  } @else {
                    No manager is set up to receive it: call your manager.
                  }
                </p>
              } @else {
                <p class="live-chat__action-hint">
                  The vendor will be approved with {{ ev.profitAmount | currency: 'USD' }} profit
                  ({{ ev.profitMarginPercent ?? 0 | number: '1.0-2' }}% margin; this customer's minimum is
                  {{ ev.floorMarginPercent | number: '1.0-2' }}%). This is recorded in Notes &amp; Activity with your name.
                </p>
              }
              <label class="live-chat__dne-override-label" for="lcDneRemark">Override remark (required)</label>
              <textarea
                id="lcDneRemark"
                class="live-chat__action-remark"
                rows="3"
                [(ngModel)]="dneOverrideRemark"
                placeholder="Why the vendor should be approved anyway…"
              ></textarea>
              @if (dneOverrideError()) {
                <p class="live-chat__action-error">{{ dneOverrideError() }}</p>
              }
              <div class="live-chat__action-modal-foot">
                <button type="button" class="live-chat__est-btn" (click)="dneOverrideStep.set('choice')" [disabled]="dneOverrideSubmitting()">
                  Back
                </button>
                <button
                  type="button"
                  class="live-chat__est-btn live-chat__dne-override-danger"
                  (click)="submitDneOverride()"
                  [disabled]="dneOverrideSubmitting() || (dneOverrideNeedsManager() && ev.managerRecipientCount === 0)"
                >
                  {{
                    dneOverrideSubmitting()
                      ? 'Sending…'
                      : dneOverrideNeedsManager()
                        ? 'Send to my manager for approval'
                        : 'Override and approve vendor'
                  }}
                </button>
              </div>
            }
          }
          @case ('called') {
            <h4 id="lcDneOverrideTitle" class="live-chat__action-title">Call your manager now</h4>
            <p class="live-chat__dne-override-call" role="alert">{{ dneOverrideEval()?.callManagerMessage }}</p>
            @if (dneOverrideSentTo()) {
              <p class="live-chat__action-hint">Approval request emailed to {{ dneOverrideSentTo() }}. You'll get an email when they decide.</p>
            }
            <div class="live-chat__action-modal-foot">
              <button type="button" class="live-chat__est-btn live-chat__est-btn--approve" (click)="closeDneOverride()">OK</button>
            </div>
          }
          @case ('pending') {
            <h4 id="lcDneOverrideTitle" class="live-chat__action-title">Waiting for your manager</h4>
            <p class="live-chat__dne-override-call" role="alert">
              Please call your manager right now. This is time-sensitive for the on-site approval for your vendor.
            </p>
            @if (dneOverrideEval()?.openOverride; as open) {
              <p class="live-chat__action-hint">
                Sent by {{ open.requestedByName }} at {{ open.requestedOn | date: 'h:mm a' }}
                @if (open.uplineEmailSentTo) {
                  <span>to {{ open.uplineEmailSentTo }}</span>
                }
              </p>
            }
            @if (dneOverrideResent()) {
              <p class="live-chat__action-hint" role="status" data-testid="dne-override-resent">{{ dneOverrideResent() }}</p>
            }
            @if (dneOverrideError()) {
              <p class="live-chat__action-error">{{ dneOverrideError() }}</p>
            }
            <div class="live-chat__action-modal-foot">
              <button type="button" class="live-chat__est-btn" (click)="withdrawDneOverride()" [disabled]="dneOverrideSubmitting()">
                Withdraw request
              </button>
              <button
                type="button"
                class="live-chat__est-btn"
                data-testid="dne-override-resend"
                (click)="resendDneOverride()"
                [disabled]="dneOverrideSubmitting()"
              >
                {{ dneOverrideSubmitting() ? 'Sending…' : 'Resend email' }}
              </button>
              <button type="button" class="live-chat__est-btn live-chat__est-btn--approve" (click)="closeDneOverride()">OK</button>
            </div>
          }
        }
      </div>
    </div>
  }
  <!-- Get Customer Approval: create + send the customer estimate here, above the pop-up (no new tab). -->
  <app-create-customer-estimate-modal
    (customerEstimateCreated)="onPopupCustomerEstimateCreated($event)"
    (sendRequested)="onPopupCustomerEstimateSendRequested($event)"
    (closed)="onPopupCustomerEstimateClosed()"
  />
  <app-estimate-vendor-deposit-modal
    (close)="onPopupVendorDepositClosed()"
    (complete)="onPopupVendorDepositComplete($event)"
  />
  <app-send-customer-estimate-modal (sent)="onPopupCustomerEstimateSent()" />
  <!-- After Approve: V1's "Additional Approval" step, which emails the vendor the approval. -->
  <app-onsite-additional-approval-modal (finished)="onAdditionalApprovalFinished($event)" />
  <!-- Get Customer Approval with 2+ open options: pick which ones go to the customer (nothing is declined). -->
  <app-customer-option-select-modal (selected)="onCustomerOptionsSelected($event)" />
  @if (checkoutConfirmOpen()) {
    <div class="live-chat__action-modal" role="dialog" aria-modal="true" aria-labelledby="lcCheckoutTitle">
      <div class="live-chat__action-modal-card">
        <h4 id="lcCheckoutTitle" class="live-chat__action-title">Check out the tech?</h4>
        <p class="live-chat__action-hint">
          The tech is checked out now, and the job stays pending until the customer approves.
        </p>
        <div class="live-chat__action-modal-foot">
          <button type="button" class="live-chat__est-btn" (click)="cancelCheckoutForApprovalLater()">Cancel</button>
          <button type="button" class="live-chat__est-btn live-chat__est-btn--approve" (click)="confirmCheckoutForApprovalLater()">
            Check out
          </button>
        </div>
      </div>
    </div>
  }
  <!-- Several open on-site options: a counter (Use counter, Accept all, manual counter) declines the other options. -->
  @if (optionPickConfirm()) {
    <div class="live-chat__action-modal" role="dialog" aria-modal="true" aria-labelledby="lcOptionPickTitle">
      <div class="live-chat__action-modal-card">
        <h4 id="lcOptionPickTitle" class="live-chat__action-title">Negotiate this option?</h4>
        <p class="live-chat__action-option-note">
          Are you sure you want to negotiate this option? Continuing will decline all other options. Only this option will remain active until it is approved.
        </p>
        <div class="live-chat__action-modal-foot">
          <button type="button" class="live-chat__est-btn" (click)="cancelOptionPick()">Cancel</button>
          <button type="button" class="live-chat__est-btn live-chat__est-btn--approve" (click)="confirmOptionPick()">
            Continue
          </button>
        </div>
      </div>
    </div>
  }
</div>
